import { useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { Segmented } from "../../shared/Segmented";
import { Sortable, useDragSensors } from "../../shared/Sortable";
import { taskWords } from "../../shared/taskWords";
import { tasksForGoal, openTasksOfGoal, byDeadline, GOAL_VIEWS, GOALS_CALENDAR_VIEW, GOALS_TOP_VIEW, RANK_VIEW_OPTIONS, placedByPreference } from "../tasks/goalOrder";
import { urgentImportantGoals, RADAR_GROUP_CAP } from "../radar/signals";
import { usePhaseAdd } from "../today/usePhaseAdd";
import { TaskListRow } from "../tasks/TaskListRow";
import { useTickTasks } from "../tasks/useTickTasks";
import { useTaskLinksOf } from "../tasks/useTaskRows";
import { GoalBlock } from "./GoalBlock";
import { RANK_COPY } from "../prioritize/ranking";
import { useRankOrder } from "../prioritize/RankStep";
import { useSeedRanks } from "../prioritize/useSeedRanks";
import { GoalCalendarView } from "./GoalCalendarView";
import { ALL, categoryChoices, inChosenCategory } from "./goalCategories";
import { NO_CATEGORY } from "../../shared/categories";
import { intro, selectStyle } from "../../shared/textStyles";

const VIEW_OPTIONS = [[GOALS_TOP_VIEW, "Top"], ...RANK_VIEW_OPTIONS, [GOALS_CALENDAR_VIEW, "Calendar"]];
// The orders Radar needs; Preference gets its ranks the first time a goal is dragged in it.
const BOTH = [GOAL_VIEWS.IMPORTANCE, GOAL_VIEWS.URGENCY];
const CALENDAR_INTRO = "Goals with a deadline are marked on the calendar. Tap a day to see the goals due that day.";
const TOP_INTRO = "Top puts your goals in order using their places on Importance and Urgency together. A goal near the top of both lists comes first. To change the order, move a goal on Importance or Urgency.";
const TOP_EMPTY = "Your goals aren't in order of importance and urgency yet. Put them in order on Importance and Urgency, and Top lists them here.";
// Top shows as many goals as Radar's group does before "Show more".
const TOP_SHOWN = RADAR_GROUP_CAP;
// The category filter is a dropdown, not chips (29 Oct 2026): a user can have many categories, and a row of chips would
// scroll and add a third strip of controls. Once a category is chosen it takes the active control's accent border, so a
// filtered page doesn't pass for the whole list.
const filterStyle = (filtered) => ({ ...selectStyle(), width: "auto", minWidth: 0, flexShrink: 1, color: C.text,
  ...(filtered ? { border: `2px solid ${C.accent}`, fontWeight: 600 } : {}) });

// Goals (the bottom bar, or Home → Goals): the top goals (Radar's Urgent & Important Goals, 23 Oct 2026), every active goal by
// importance, by urgency, by preference (the order the user would like to work on them) or on a calendar, with its unfinished tasks folded under it. Since 21 Oct 2026
// it is where goals are ranked: Prioritize was retired and its switch and words moved here, and the task
// views moved to the Tasks page (Craig: "replace the dropdown filter top-left with the full-width toggle from
// Prioritize, AND add a 3rd option 'Calendar'").
// Opening it ranks any unranked goal on both orders, which Radar needs. onRank(view, ordered) saves an order
// and returns a promise. `view` and onView(view) remember the view. The editors live in App.jsx: onOpenGoal,
// onOpenTask, onAddTask(goal) and onDone(task) reach them. saveTask(taskId, fields) and `phase` (the part of
// the day it is now) put a task in today. `categories` are the user's, for the category filter, which shows when there
// are two or more categories to choose from ("No category" counts) and applies to every view, and to the tasks without
// a goal by their own category. It lasts while the page is open.
export function GoalsPage({ goals, tasks, categories = [], view, onView, onRank, onDone, onOpenGoal, onOpenTask, onAddTask, saveTask, phase, onNav }) {
  const phaseAdd = usePhaseAdd(saveTask, phase);
  const active = useMemo(() => (goals || []).filter(g => g.status === "active"), [goals]);
  const [openGoals, setOpenGoals] = useState(() => new Set()); // goals start folded
  const { isTicked, tick } = useTickTasks(onDone);
  const linksOf = useTaskLinksOf();
  const [failed, setFailed] = useState(() => new Set()); // the orders whose last save failed
  const [allTop, setAllTop] = useState(false);
  const choices = useMemo(() => categoryChoices(active, categories), [active, categories]);
  const [chosen, setChosen] = useState(ALL);
  // Back to every goal when the chosen category no longer has one, or there is nothing to choose between.
  const category = choices.length > 1 && choices.some(c => c.id === chosen) ? chosen : ALL;
  const inView = (item) => inChosenCategory(item, category, categories);
  const sensors = useDragSensors();
  // The order on screen, kept here so a drag shows at once and "Try again" re-sends what the user sees.
  const importance = useRankOrder(GOAL_VIEWS.IMPORTANCE, active);
  const urgency = useRankOrder(GOAL_VIEWS.URGENCY, active);
  const preference = useRankOrder(GOAL_VIEWS.PREFERENCE, active);
  const rankFor = (v) => ({ [GOAL_VIEWS.IMPORTANCE]: importance, [GOAL_VIEWS.URGENCY]: urgency, [GOAL_VIEWS.PREFERENCE]: preference })[v];
  const ranked = Object.values(GOAL_VIEWS).includes(view); // an order the user drags
  const top = useMemo(() => urgentImportantGoals(active).map(r => r.goal), [active]);

  const fail = (e, v) => { console.error("Saving goal order failed:", e); setFailed(s => new Set([...s, v])); };
  useSeedRanks(BOTH, active, v => rankFor(v).goalsInOrder(), onRank, fail);
  // Try again re-sends the orders Radar needs and any other order whose save failed, never one the user
  // hasn't set (that would fix Preference as a copy of Importance).
  const retry = () => {
    const views = [...new Set([...BOTH, ...failed])];
    setFailed(new Set());
    views.forEach(v => onRank(v, rankFor(v).goalsInOrder()).catch(e => fail(e, v)));
  };
  // Until the user places a goal by preference, Preference starts from the importance order on screen, which
  // may include a drag the listener hasn't sent back yet.
  const chooseView = (v) => {
    if (v === GOAL_VIEWS.PREFERENCE && !placedByPreference(active)) preference.setOrder(importance.shown);
    onView(v);
  };
  const onDragEnd = ({ active: moved, over }) => {
    if (!over || moved.id === over.id) return;
    const rank = rankFor(view);
    const next = arrayMove(rank.shown, rank.shown.indexOf(moved.id), rank.shown.indexOf(over.id));
    rank.setOrder(next);
    setFailed(s => { const n = new Set(s); n.delete(view); return n; });
    onRank(view, next.map(id => rank.byId.get(id))).catch(e => fail(e, view));
  };

  const open = useMemo(() => (tasks || []).filter(t => !t.done).sort(byDeadline(goals)), [tasks, goals]);
  const activeIds = new Set(active.map(g => g.id));
  const withoutGoal = open.filter(t => !activeIds.has(t.parentGoalId) && inView(t));
  const toggle = (id) => setOpenGoals(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const row = (task) => <TaskListRow key={task.id} task={task} links={linksOf(task)} done={isTicked(task.id)} onDone={tick} onOpen={onOpenTask}
    onAddToday={phaseAdd.addNow} />;

  const goalBlock = (goal, { drag = null, dated = false } = {}) => (
    <GoalBlock goal={goal} words={taskWords(tasksForGoal(tasks, goal.id))} openTasks={openTasksOfGoal(open, goal.id)}
      open={openGoals.has(goal.id)} onToggle={() => toggle(goal.id)} onOpenGoal={() => onOpenGoal(goal)}
      onAddTask={() => onAddTask(goal)} row={row} dated={dated} drag={drag} />
  );
  const shown = ranked ? rankFor(view).goalsInOrder().filter(inView) : [];
  const topShown = top.filter(inView);
  // What Top says when none of its goals are in the chosen category.
  const noTopWords = `${category === NO_CATEGORY ? "Every one of your top goals has a category."
    : `None of your top goals are in ${categories.find(c => c.id === category)?.label}.`} Choose All categories to see them.`;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text }}>
      <TopBar title="Goals" help={HELP.goals} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ ...pageColumn, padding: "12px 20px 40px" }}>
          {active.length === 0 ? (
            <p style={intro()}>You don't have any goals yet. Tap + at the top to add one.</p>
          ) : (
            <>
              <Segmented label="Show goals by" stretch options={VIEW_OPTIONS} value={view} onChange={chooseView} />
              <p style={{ ...intro(), margin: "14px 0" }}>{ranked ? RANK_COPY[view].body : view === GOALS_TOP_VIEW ? TOP_INTRO : CALENDAR_INTRO}</p>
              {choices.length > 1 && (
                <div style={{ margin: "0 0 14px" }}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <select aria-label="Show goals in" value={category} onChange={e => setChosen(e.target.value)} style={filterStyle(category !== ALL)}>
                      <option value={ALL}>All categories</option>
                      {choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                  {ranked && category !== ALL && (
                    <p style={{ ...intro(), margin: "8px 0 0", textAlign: "center" }}>When you drag a goal here, it moves among all your goals.</p>
                  )}
                </div>
              )}
              {failed.size > 0 && (
                <p role="alert" style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.5, color: C.red }}>
                  We couldn't save this order. Check your connection and try again.{" "}
                  <button onClick={retry} style={{ minHeight: 44, padding: "0 4px", background: "none", border: "none", color: C.text, fontSize: 15, fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>Try again</button>
                </p>
              )}
              {ranked ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={shown.map(g => g.id)} strategy={verticalListSortingStrategy}>
                    {shown.map(goal => <Sortable key={goal.id} id={goal.id}>{(drag) => goalBlock(goal, { drag })}</Sortable>)}
                  </SortableContext>
                </DndContext>
              ) : view === GOALS_TOP_VIEW ? (
                <>
                  {top.length === 0 && <p style={intro()}>{TOP_EMPTY}</p>}
                  {top.length > 0 && topShown.length === 0 && <p style={intro()}>{noTopWords}</p>}
                  {(allTop ? topShown : topShown.slice(0, TOP_SHOWN)).map(goal => <div key={goal.id}>{goalBlock(goal)}</div>)}
                  {topShown.length > TOP_SHOWN && (
                    <button onClick={() => setAllTop(a => !a)}
                      style={{ width: "100%", minHeight: 44, marginTop: 4, background: "none", border: "none", fontSize: 16, fontWeight: 600, color: C.accent, cursor: "pointer", fontFamily: "inherit" }}>
                      {allTop ? "Show less" : `Show ${topShown.length - TOP_SHOWN} more`}
                    </button>
                  )}
                </>
              ) : (
                <GoalCalendarView goals={(goals || []).filter(inView)} renderGoals={(list, { dated }) => list.map(goal => <div key={goal.id}>{goalBlock(goal, { dated })}</div>)} />
              )}
              {ranked && withoutGoal.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h2 style={{ margin: 0, padding: "8px 0 4px", fontSize: 13, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>Tasks without a goal</h2>
                  {withoutGoal.map(row)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {phaseAdd.toast}
      <BottomNav active="goals" onNav={onNav} />
    </div>
  );
}
